import React, { useEffect, useState, useRef } from 'react';
import parse from 'html-react-parser';
import { withStyles } from '@material-ui/core/styles';
import { Paper, Card, CardHeader, CardContent, CardActions, Typography, Button, Divider, Box, Grid, Hidden, Chip } from '@material-ui/core';
import ArrowForwardIosIcon from '@material-ui/icons/ArrowForwardIos';
import CameraAltIcon from '@material-ui/icons/CameraAlt'; 
import CloudUploadIcon from '@material-ui/icons/CloudUpload';
import DeleteIcon from '@material-ui/icons/Delete'; // ← Aggiungi per rimuovere il file
import blue from '@material-ui/core/colors/blue';
import QuestionType from './QuestionType/QuestionType';
import MediaType from './MediaType/MediaType';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import ChallengeInfo from './ChallengeInfo/ChallengeInfo';
import ChallengeHint from './ChallengeHint/ChallengeHint';
import StepProgressBar from './StepProgressBar/StepProgressBar';
import Carousel from '../Carousel/Carousel';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import { useUploadFile } from "../../../hooks/useUploadFile";
import useAppStore from '../../../store/appStore';

const styles = theme => ({
  paper: {
    minHeight: 620,
    height: '100%',
    borderRadius: 6,
    position: 'relative'
  },
  card: {
    boxShadow: 'none',
    background: 'transparent',
    borderRadius: 0,
    height: '100%',
    paddingBottom: 100,
    [theme.breakpoints.down(1500)]: {
      paddingBottom: 188
    }
  },
  cardHeader: {
    padding: theme.spacing(3),
    '& > div > h2': {
      fontSize: theme.typography.pxToRem(24),
      fontWeight: theme.typography.fontWeightMedium
    }
  },
  cardContent: {
    padding: theme.spacing(5, 3, 3, 3)
  },
  cardAction: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    borderTop: `solid 1px ${theme.palette.divider}`,
    padding: theme.spacing(2.8, 3),
    // Su mobile: cambia da flex row a flex column e rimuovi absolute
    [theme.breakpoints.down('sm')]: {
      position: 'relative',
      flexDirection: 'column',
      alignItems: 'stretch'
    }
  },
  buttonContained: {
    backgroundColor: blue[800],
    '&:hover': {
      backgroundColor: blue[900]
    }
  },
  uploadButton: {
    marginRight: '16px',
    minWidth: 140,
    color: blue[800],
    borderColor: blue[800],
    [theme.breakpoints.down('xs')]: {
      minWidth: 'auto', // Rimuove minWidth su mobile
      flex: 1, // Occupa spazio disponibile
    },
    '&:hover': {
      backgroundColor: blue[900],
      color: theme.palette.common.white,
    },
    '&:focus': {
      outline: 'none',
    },
    '&:active': {
      backgroundColor: blue[900],
      color: theme.palette.common.white,
    },
    // Su mobile: rimuovi margin destro e aggiungi margin bottom per il gap
    [theme.breakpoints.down('sm')]: {
      marginRight: 0,
      
      width: '100%', // Larghezza piena su mobile
    }
  },
  
  buttonsContainer: {
    display: 'flex',
    alignItems: 'center',
    // Su mobile: colonna invece che riga e ordine specifico
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
      alignItems: 'stretch', // I bottoni prendono tutta la larghezza
      width: '100%',
      order: 2, // I bottoni upload/next vanno per secondi
      gap: 0, // Rimuovi gap del container, gestiamo con margin
    }
  },
  
  nextButton: {
    // Su mobile: tutta la larghezza
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      marginTop: theme.spacing(1.5), // Gap di 12px tra i bottoni
    }
  },
  
  // Modifica per la colonna sinistra (ChallengeInfo + ChallengeHint)
  leftColumn: {
    [theme.breakpoints.down('sm')]: {
      order: 1, // ChallengeInfo va per primo (sopra i bottoni)
      width: '100%',
      display: 'flex',
      flexDirection: 'column', // Elementi in colonna su mobile
      alignItems: 'stretch', // Larghezza piena
      marginBottom: theme.spacing(1.5), // Gap tra ChallengeInfo e i bottoni
    }
  },
  
  question: {
    fontWeight: theme.typography.fontWeightMedium,
    lineHeight: 1.62,
    letterSpacing: 0.2,
    '& ul': {
      marginBottom: theme.spacing(2),
      padding: theme.spacing(0, 0, 0, 4)
    },
    '& li': {
      marginBottom: theme.spacing(1.5)
    },
    '& a': {
      color: theme.palette.common.gamsPetroleumBlue,
      '&:hover': {
        color: theme.palette.common.blue[400],
        textDecoration: 'underline'
      }
    }
  },
  videoTitle: {
    fontWeight: theme.typography.fontWeightMedium,
    lineHeight: 1.62,
    letterSpacing: 0.2,
    marginBottom: 20
  },
  innerBox: {
    maxWidth: 600,
    margin: '0 auto'
  },
  innerBoxCarousel: {
    maxWidth: 'auto',
    margin: '0 auto'
  },
  gridContainer: {
    flexGrow: 1
  },
  
  // NUOVO: Stile per il container del bottone upload + file
  uploadSection: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    position: 'relative', // ← Aggiungi per contenere l'absolute
    [theme.breakpoints.up('sm')]: {
      alignItems: 'flex-start',
      width: 'auto',
    }
  },
  
  // NUOVO: Stile per il chip del file piccolo e ancorato
  uploadedFileChip: {
    marginTop: theme.spacing(0.1), 
    marginBottom: theme.spacing(0.2), // 8px di margin bottom
    height: 18, // Altezza ridotta
    fontSize: theme.typography.pxToRem(9), // Font più piccolo
    '& .MuiChip-icon': {
      width: 14,
      height: 14,
      marginLeft: theme.spacing(0.5),
    },
    '& .MuiChip-deleteIcon': {
      width: 14,
      height: 14,
      marginRight: theme.spacing(0.5),
    },
    '& .MuiChip-label': {
      paddingLeft: theme.spacing(0.5),
      paddingRight: theme.spacing(0.5),
      fontSize: theme.typography.pxToRem(9),
    },
    // Su desktop: position absolute per non interferire
    [theme.breakpoints.up('md')]: {
      position: 'absolute',
      top: '100%', // Subito sotto il bottone
      left: 0,
      marginTop: theme.spacing(0.5),
      marginBottom: 0,
      zIndex: 1,
    },
    // Su mobile: comportamento normale
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      justifyContent: 'space-between',
      marginTop: theme.spacing(0.5),
      position: 'relative', // Assicurati che sia relative su mobile
    }
  },
});

const ChallengeBlock = props => {
  const {
    challenge,
    history,
    classes,
    stepIndex,
    hasMedia,
    onChanged,
    onSkipStep,
    hasCarousel,
    isSubmitting,
    onSubmit // ← Ricevi onSubmit come prop
  } = props;
  const matchesMediaQuery = useMediaQuery(theme => theme.breakpoints.up('lg'));
  const step = challenge.steps;
  const [disabled, setDisabled] = useState(false);
  const challengeInfoRef = useRef();
  const user = useAppStore((state) => state.user);
  // useUploadPhoto con invalidateUserData: false per non refreshare i dati utente durante l'upload nelle challenge
  const uploadPhotoMutationForChallenge = useUploadFile({ invalidateUserData: false });

  useEffect(() => {
    setDisabled(isSubmitting || uploadPhotoMutationForChallenge.isPending);
  }, [isSubmitting, uploadPhotoMutationForChallenge.isPending]);

  const currentStep = step[stepIndex];
  const currentQuestionField = currentStep.questionField;
  const currentVideoTitle = currentStep.videoTitle;
  
  // NUOVO: State per gestire il file caricato
  const [uploadedFile, setUploadedFile] = useState(null); // { name, base64, size, type }
  const fileInputRef = useRef(null);


  // Funzione per gestire l'upload della foto (migliorata da ValidationPopover.jsx)
  const handleUploadPhoto = () => {
    if (isMobileDevice()) {
      // Su mobile: apri la fotocamera
      if (fileInputRef.current) {
        fileInputRef.current.setAttribute('capture', 'environment');
        fileInputRef.current.setAttribute('accept', 'image/*');
        fileInputRef.current.click();
      }
    } else {
      // Su desktop: apri il file picker
      if (fileInputRef.current) {
        fileInputRef.current.removeAttribute('capture');
        fileInputRef.current.setAttribute('accept', 'image/*');
        fileInputRef.current.click();
      }
    }
  };

  // Funzione per gestire il cambio del file 
  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (file) {
      // Salviamo SOLO il file (non un oggetto)
      setUploadedFile(file);
      
      // Reset del file input per permettere di caricare lo stesso file più volte
      event.target.value = '';
    }
  };

  // NUOVO: Funzione per rimuovere il file caricato (da ValidationPopover.jsx)
  const handleRemoveFile = () => {
    setUploadedFile(null);
  };

  // NUOVO: Funzione helper per determinare se siamo su mobile
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
           (window.innerWidth <= 768);
  };

  // Funzione helper per determinare lo stato del bottone
  const getButtonState = () => {
    const blockedSteps = challenge.blockedSteps || [];
    const isCurrentStepBlocked = blockedSteps.includes(stepIndex);
    const isLastStep = step.length === stepIndex + 1;
    // Usa direttamente challenge.status che viene aggiornato dalle props
    const challengeStatus = challenge.status;

    // Determina il tipo di azione e il testo del bottone
    if (isCurrentStepBlocked) {
      if (challengeStatus === 'IN_VALIDATION') {
        return {
          text: 'Revoca Validazione',
          action: 'revokeValidation',
          status: 'IN_PROGRESS' 
        };
      } else {
        return {
          text: 'Richiedi Validazione',
          action: 'requestValidation',
          status: 'IN_VALIDATION'
        };
      }
    }

    // Logica normale per step non bloccati
    return {
      text: isLastStep ? 'Finish' : 'Next Step',
      action: isLastStep ? 'finish' : 'next',
      status: isLastStep ? 'FINISHED' : null
    };
  };

  // Funzione per determinare il testo del bottone (ora più semplice)
  const getButtonText = () => {
    return getButtonState().text;
  };

  // Controlla se lo step corrente richiede upload file
  const isUploadFileStep = currentStep.questionType === 'uploadFile';

  // Funzione di click più elegante e flessibile
  const handleNextStepClick = async () => {
    const stepTime = challengeInfoRef.current?.getStepTimeInSeconds() || 0;
    const buttonState = getButtonState();

    let response = null;

    // Se è uploadFile E c'è un file, carica il file PRIMA
    if (isUploadFileStep && uploadedFile &&  challenge.status === 'IN_PROGRESS') {
      try {
        
        const uploadResult = await uploadPhotoMutationForChallenge.mutateAsync({
          file: uploadedFile, 
          path: 'gams-uat/challenge/steps', 
          startWith: 'step',
          fileName: user?.me ? user.me.email + '_' + challenge?.challengeTitle + '_step_' + (stepIndex + 1).toString() : '',
        }); 
        response = uploadResult.uploadPhoto.url ?? '';
        
      } catch (error) {
        
        alert('Errore durante il caricamento del file. Riprova.');
        return;
      }
    }

    switch (buttonState.action) {
      case 'requestValidation':
        // Richiedi validazione - passa la response dell'upload se presente, NON andare avanti, FLAG isValidationAction
        onSubmit(step.length, stepTime, buttonState.status, response, false, true, false);
        break;
        
      case 'revokeValidation':
        // Revoca validazione - NON serve uploadare file, solo cambiare status a IN_PROGRESS, NON andare avanti, FLAG isRevokeAction
        onSubmit(step.length, stepTime, buttonState.status, null, false, false, true);
        break;
        
      case 'next':
      case 'finish':
      default:
        // Azione normale (next step o finish) - passa la response dell'upload se presente, VAI avanti
        onSubmit(step.length, stepTime, buttonState.status, response, true, false, false);
        break;
    }

    // Disabilita il bottone se è l'ultimo step
    if (step.length === stepIndex + 1) {
      setDisabled(true);
    }
  };

  return (
    <Paper className={classes.paper}>
      <Card className={classes.card}>
        <StepProgressBar steps={step} stepIndex={stepIndex} />

        <CardHeader
          title={`Step ${stepIndex + 1}`}
          titleTypographyProps={{ variant: 'h2', component: 'h2' }}
          classes={{ root: classes.cardHeader }}
        />
        <Divider component='div' />

        {disabled ? (
          <CardContent className={classes.cardContent}>
            <LoadingSpinner />
          </CardContent>
        ) : (
          <CardContent className={classes.cardContent}>
            <Grid container spacing={3} className={classes.gridContainer}>
              {hasMedia ? (
                <Hidden only={['xs', 'sm', 'md']}>
                  <Grid
                    item
                    xs={currentQuestionField ? 6 : 12}
                    style={
                      currentQuestionField
                        ? null
                        : { marginBottom: -14 }
                    }
                  >
                    {currentVideoTitle ? (
                      <Box className={classes.innerBox}>
                        <Typography
                          component='h3'
                          variant='body1'
                          color='inherit'
                          className={classes.videoTitle}
                        >
                          {currentVideoTitle}
                        </Typography>
                      </Box>
                    ) : null}
                    <Box className={classes.innerBox}>
                      <MediaType
                        currentStep={currentStep}
                        stepIndex={stepIndex}
                      />
                    </Box>
                  </Grid>
                </Hidden>
              ) : null}

              <Grid
                item
                xs={
                  hasMedia && matchesMediaQuery && currentQuestionField
                    ? 6
                    : 12
                }
              >
                {currentQuestionField ? (
                <Box className={classes.innerBox}>
                  <Typography
                    component='div'
                    variant='body1'
                    color='inherit'
                    className={classes.question}
                  >
                    {parse(currentQuestionField)}
                  </Typography>
                </Box>
                ) : null}

                {hasMedia ? (
                  <Hidden only={['lg', 'xl', 'xxl']}>
                    <Box className={classes.innerBox}>
                      {currentVideoTitle ? (
                        <Box className={classes.innerBox}>
                          <Typography
                            component='h3'
                            variant='body1'
                            color='inherit'
                            className={classes.videoTitle}
                          >
                            {currentVideoTitle}
                          </Typography>
                        </Box>
                      ) : null}
                      <Box mt={2}>
                        <MediaType
                          currentStep={step[stepIndex]}
                          stepIndex={stepIndex}
                        />
                      </Box>
                    </Box>
                  </Hidden>
                ) : null}

                {hasCarousel ? (
                  <Box className={classes.innerBoxCarousel}>
                    <Carousel carouselSlides={step[stepIndex].imageCarousel} />
                  </Box>
                ) : null}

                <Box className={classes.innerBox}>
                  <QuestionType
                    currentStep={step[stepIndex]}
                    onChanged={onChanged}
                    stepIndex={stepIndex}
                  />
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        )}

        <CardActions 
          className={classes.cardAction}
          disableSpacing={true}
        >
          <Box flexGrow={1} className={classes.leftColumn}>
            <ChallengeInfo
              ref={challengeInfoRef}
              skip={onSkipStep}
              history={history}
              step={stepIndex}
              time={step[stepIndex].countdownTime}
              description={challenge.challengeDescription}
              hasMedia={hasMedia}
              timerEnabled={challenge.timerEnabled}
              coach={challenge.coach}
              challengeTitle={challenge.challengeTitle}
              challengeIsValidation={challenge.isValidation}
              challengeDifficulty={challenge.challengeDifficulty}
              challengeAverageTime={challenge.averageTime}
              blockedSteps={challenge.blockedSteps}
            />
            {step[stepIndex].HintTitle ? (
              <ChallengeHint currentStep={step[stepIndex]} />
            ) : null}
          </Box>
          
          <Box className={classes.buttonsContainer}>
            {/* Input nascosto per il file upload */}
            <input
              ref={fileInputRef}
              type="file"
              style={{ display: 'none' }}
              onChange={handleFileChange}
              accept="image/*"
            />
            
            {/* Container per bottone upload + file caricato */}
            {isUploadFileStep && (
              <Box className={classes.uploadSection}>
                {/* Bottone Upload foto */}
                <Button 
                  variant="outlined"
                  onClick={handleUploadPhoto}
                  startIcon={isMobileDevice() ? <CameraAltIcon /> : <CloudUploadIcon />}
                  className={classes.uploadButton}
                  size="large"
                  disabled={disabled}
                >
                  {isMobileDevice() ? 'Scatta foto' : 'Carica foto'}
                </Button>
                
                {/* File caricato SOTTO il bottone, piccolo e ancorato */}
                {uploadedFile && (
                  <Chip
                    icon={<CameraAltIcon />}
                    label={uploadedFile.name}
                    onDelete={handleRemoveFile}
                    deleteIcon={<DeleteIcon />}
                    variant="outlined"
                    color="primary"
                    size="small"
                    className={classes.uploadedFileChip}
                  />
                )}
              </Box>
            )}
            
            {/* Bottone Next Step */}
            <Button
              variant='contained'
              color='primary'
              className={`${classes.buttonContained} ${classes.nextButton}`}
              size='large'
              endIcon={<ArrowForwardIosIcon />}
              disabled={disabled}
              onClick={handleNextStepClick}
            >
              {getButtonText()}
            </Button>
          </Box>
        </CardActions>
      </Card>
    </Paper>
  );
}

export default withStyles(styles)(ChallengeBlock);
